import { Component, Inject, OnInit, Optional } from '@angular/core';
import { CellClickedEvent } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { LayoutService } from '@app/core/services/layout.service';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DataMiningColumn, DataMiningGet, DataMiningItem, DataMiningService } from '@app/services/data-mining.service';
import { AgCartesianSeriesOptions, AgChartOptions } from 'ag-charts-community';

@Component({
  selector: 'tt-data-mining',
  templateUrl: './data-mining.component.html',
  styleUrls: ['./data-mining.component.css'],
  standalone: false,
})
export class DataMiningComponent implements OnInit {
    public page?: DataMiningGet;
    public grid?: GridOptions;
    public groupings: DataMiningColumn[] = [];
    public numbers: DataMiningColumn[] = [];
    public numberColumns1: DataMiningColumn[] = [];
    public numberColumns2: DataMiningColumn[] = [];
    public numberColumns3: DataMiningColumn[] = [];
    public miningData: DataMiningItem[] = [];
    public chartOptions?: AgChartOptions;
    public charts: AgChartOptions[] = [];
    public selectedView = 'GRAPH';
    public loading = false;
    public navigating = false;
    public readonly desktopSize: MediaQueryList;
    private loadVersion = 0;

    constructor(
        private mining: DataMiningService,
        private remember: RememberService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.desktopSize = layout.getMediaQueries().tablet;
    }

    public get chartRememberId(): string | undefined {
        return this.page?.grouping_colname ? 'datamining.' + this.page.grouping_colname : undefined;
    }

    public get listData(): DataMiningItem[] {
        return [...this.miningData].sort((a, b) => this.compareOrder(a.orderby, b.orderby));
    }

    public async ngOnInit(): Promise<void> {
        const params = this.navigator?.params;
        if (params?.['p2_statsource_keyno'] === undefined || params['level_no'] === undefined) return;
        this.loading = true;
        try {
            this.page = await this.mining.get({ p2_statsource_keyno: String(params['p2_statsource_keyno']), level_no: String(params['level_no']) });
            if (!this.page) return;
            this.navigator?.setHeading?.(this.page.statsource_name);
            const columnParams = { p2_statsource_keyno: this.page.p2_statsource_keyno, level_no: this.page.level_no };
            const [groupings, numbers] = await Promise.all([this.mining.listGroupings(columnParams), this.mining.listNumbers(columnParams)]);
            this.groupings = groupings;
            this.numbers = numbers;
            this.filterNumberColumns();
            this.grid = new Grid()
                .setLoadDataFunction({ method: 3161, parameters: () => this.page })
                .setConfig({ rowClick: true })
                .setSpecialFunc({ newTab: true })
                .setKendoConfig({ height: null });
            await this.loadMiningData();
        } finally {
            this.loading = false;
        }
    }

    public async onColumnChanged(value: string, field: 'grouping_colname' | 'number_colname1' | 'number_colname2' | 'number_colname3'): Promise<void> {
        if (!this.page) return;
        this.page[field] = value;
        if (field === 'number_colname2' && value === '') this.page.number_colname3 = '';
        if (field === 'grouping_colname') await this.rememberChartType();
        this.filterNumberColumns();
        this.grid?.gridfunc?.rebind();
        await this.loadMiningData();
    }

    private filterNumberColumns(): void {
        const page = this.page;
        if (!page) return;
        this.numberColumns1 = this.numbers.filter((column) => ![page.number_colname2, page.number_colname3].includes(column.grouping_colname));
        this.numberColumns2 = this.numbers.filter((column) => ![page.number_colname1, page.number_colname3].includes(column.grouping_colname));
        this.numberColumns3 = this.numbers.filter((column) => ![page.number_colname1, page.number_colname2].includes(column.grouping_colname));
    }

    private async loadMiningData(): Promise<void> {
        if (!this.page?.grouping_colname?.trim() || !this.page.number_colname1?.trim() || this.page.number_colname2 == null || this.page.number_colname3 == null) return;
        const version = ++this.loadVersion;
        this.miningData = [];
        this.updateChart();
        const miningData = await this.mining.listMiningData({ ...this.page });
        if (version !== this.loadVersion) return;
        this.miningData = miningData;
        this.updateChart();
    }

    public onChartTypeChanged(value: string): void {
        if (!this.page) return;
        this.page.chart_type = value;
        this.updateChart();
    }

    private rememberChartType(): Promise<void> | undefined {
        if (this.chartRememberId && this.page?.chart_type) return this.remember.remember(this.chartRememberId, this.page.chart_type);
        return undefined;
    }

    public goTo(state: string, event?: MouseEvent): void {
        if (!this.page) return;
        const target = { to: state, parms: { p2_statsource_keyno: this.page.p2_statsource_keyno, level_no: this.page.level_no } };
        if (event?.ctrlKey) this.navigator?.newTab(target);
        else this.navigator?.go(target);
    }

    public async fetchItem(item: DataMiningItem): Promise<void> {
        await this.rememberChartType();
        if (item.gotolink === '0') await this.drillDown(item.item_name);
        else if (item.item_state) await this.navigator?.go({ to: item.item_state, parms: item.item_parms });
    }

    public async onGridCellClick(event: CellClickedEvent<Record<string, unknown>>): Promise<void> {
        if (!this.page || !event.data) return;
        await this.drillDown(String(event.data[this.page.grouping_colname] ?? ''));
    }

    private async drillDown(itemName: string): Promise<void> {
        if (!this.page || this.navigating) return;
        this.navigating = true;
        try {
            const nextLevel = await this.mining.addNextLevel({ ...this.page, item_name: itemName });
            if (nextLevel) await this.navigator?.go({ to: nextLevel.webpagename, parms: nextLevel.parms });
        } finally {
            this.navigating = false;
        }
    }

    public updateChart(): void {
        const fields = this.miningData[0]?.item_secondary
            .map((value, index) => ({ value, index }))
            .filter(({ value }) => value.visible_chart === true || value.visible_chart === 'true') ?? [];
        const data = this.miningData.slice(1).filter((item) => item.item_id !== '-1').map((item, index) => {
            const values: Record<string, number> = {};
            item.item_secondary.forEach((value, column) => values['item_col' + column] = parseFloat(String(value.item_col)));
            return { ...item, ...values, orderby: item.orderby ?? index + 1 };
        }).sort((a, b) => this.compareOrder(a.orderby, b.orderby));
        const type = this.page?.chart_type;
        const pie = ['pie', 'L', 'E', 'C'].includes(type ?? '');
        const dark = document.documentElement.classList.contains('dark');
        const labelColor = dark ? '#f5f5f5' : '#222222';
        const nodeClick = ({ datum }: { datum: DataMiningItem }) => { void this.fetchItem(datum); };
        const base = {
            data, background: { visible: false }, legend: { position: 'bottom' as const }, height: 400,
            theme: dark ? 'ag-default-dark' as const : 'ag-default' as const,
        };
        if (pie) {
            this.charts = fields.map(({ value, index }) => ({
                ...base,
                title: { text: String(value.item_col), color: labelColor },
                series: [{
                    type: 'pie', angleKey: 'item_col' + index, angleName: String(value.item_col),
                    calloutLabelKey: 'item_name', calloutLabel: { color: labelColor },
                    sectorLabelKey: 'item_col' + index, sectorLabel: { color: labelColor },
                    listeners: { nodeClick },
                }],
            }));
        } else {
            const barColors = ['#ff6800', '#a0a700', '#ffb53c'];
            const series = fields.map<AgCartesianSeriesOptions>(({ value, index }, seriesIndex) => {
                const options = {
                    xKey: 'item_name', yKey: 'item_col' + index, yName: String(value.item_col),
                    label: { enabled: true, color: labelColor }, listeners: { nodeClick },
                };
                if (type === 'line') return { ...options, type: 'line' };
                const color = barColors[seriesIndex % barColors.length];
                return { ...options, type: 'bar', fill: color, stroke: color, label: { ...options.label, placement: 'outside' } };
            });
            this.charts = [{ ...base, series, axes: [{ type: 'category', position: 'bottom', label: { rotation: -60 } }, { type: 'number', position: 'left' }] }];
        }
        this.chartOptions = this.charts[0];
    }

    private compareOrder(a?: string | number, b?: string | number): number {
        if (Number.isFinite(Number(a)) && Number.isFinite(Number(b))) return Number(a) - Number(b);
        return String(a ?? '').localeCompare(String(b ?? ''));
    }

}
